import { useCallback } from 'react';

/**
 * Returns a ref callback that, when attached to a DOM node, smooth-scrolls
 * that node into the nearest visible position of its scroll container
 * after a 250 ms debounce.
 *
 * Skips scrolling when the target is already fully inside its overflow
 * container (so mere selection of an already-visible item doesn't yank
 * the list around). Used by both the store list (`StoreDetails`) and the
 * thana list (`LocationModal`).
 */
export function useScrollToView<T, K>(isOpen: boolean, selectedItem: T, list: K[]) {
  const callbackRef = useCallback(
    (node: HTMLElement | null) => {
      if (node && isOpen) {
        const rect = node.getBoundingClientRect();
        const container = node.closest('.overflow-y-auto') as HTMLElement | null;

        // No scrollable ancestor — fall back to the original behavior
        // (used by the default thana branch in StoreDetails and as a
        // defense-in-depth for transient regression windows).
        if (!container) {
          const timer = setTimeout(() => {
            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 250);
          return () => clearTimeout(timer);
        }

        const containerRect = container.getBoundingClientRect();
        const isVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

        if (!isVisible) {
          const timer = setTimeout(() => {
            node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }, 250);
          return () => clearTimeout(timer);
        }
      }
    },
    [isOpen, selectedItem, list],
  );

  return callbackRef;
}
